// ReportTypeSelectionPage.tsx
// Step 2 in the new flow: Premises → [Report Type] → Month → Report
// Shows 5 report type cards with total record counts for the selected premise.
// Also has a "View Trends" button per card.
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { usePremisesStore } from "../../store/store";
import { DotLoader } from "../../components/loader/Loader";
import { REPORT_TYPES, safeCount, getPremiseName } from "./constants";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ClipboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden="true">
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M9 12h6M9 16h4M9 8h6" />
  </svg>
);
const FlameIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden="true">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);
const BoltIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const SnowflakeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden="true">
    <line x1="12" y1="2" x2="12" y2="22" /><path d="M17 5l-5 5-5-5M17 19l-5-5-5 5" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M5 7l5 5-5 5M19 7l-5 5 5 5" />
  </svg>
);
const BarChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden="true">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /><line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);
const WrenchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden="true">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);
const TrendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
  </svg>
);

const REPORT_ICONS: Record<string, React.ReactNode> = {
  "mis-reports": <ClipboardIcon />,
  "fire-safety-scorecard": <FlameIcon />,
  "electrical-scorecard": <BoltIcon />,
  "split-ac": <SnowflakeIcon />,
  "kpi-scorecard": <BarChartIcon />,
  "equipment-inspections": <WrenchIcon />,
};

const ICON_STYLES: Record<string, { bg: string; text: string; accent: string; border: string }> = {
  "mis-reports": { bg: "bg-blue-50", text: "text-blue-500", accent: "bg-blue-500", border: "border-blue-200" },
  "fire-safety-scorecard": { bg: "bg-orange-50", text: "text-orange-500", accent: "bg-orange-500", border: "border-orange-200" },
  "electrical-scorecard": { bg: "bg-yellow-50", text: "text-yellow-500", accent: "bg-yellow-500", border: "border-yellow-200" },
  "split-ac": { bg: "bg-cyan-50", text: "text-cyan-500", accent: "bg-cyan-500", border: "border-cyan-200" },
  "kpi-scorecard": { bg: "bg-purple-50", text: "text-purple-500", accent: "bg-purple-500", border: "border-purple-200" },
  "equipment-inspections": { bg: "bg-green-50", text: "text-green-600", accent: "bg-green-500", border: "border-green-200" },
};

// ─── ReportTypeSelectionPage ──────────────────────────────────────────────────
const ReportTypeSelectionPage: React.FC = () => {
  const { premiseId } = useParams<{ premiseId: string }>();
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();
  const [totalCounts, setTotalCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const premiseName = getPremiseName(premiseId ?? "", allPremises);

  useEffect(() => {
    if (!allPremises.length) fetchPremises();
  }, []);

  useEffect(() => {
    if (!premiseId) return;
    setLoading(true);
    Promise.all(
      REPORT_TYPES.map(rt => safeCount(rt.apiEndpoint, { premisesId: premiseId }))
    ).then(results => {
      const counts: Record<string, number> = {};
      REPORT_TYPES.forEach((rt, i) => { counts[rt.key] = results[i]; });
      setTotalCounts(counts);
    }).finally(() => setLoading(false));
  }, [premiseId]);

  return (
    <div className="w-full px-6 mt-20 pb-10">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
          <li><Link to="/fire-system/monitoring" className="hover:text-[#00A8A5] font-medium transition-colors">Monitoring</Link></li>
          <li className="text-gray-300 select-none">›</li>
          <li aria-current="page" className="text-gray-800 font-semibold truncate max-w-xs">{premiseName}</li>
        </ol>
      </nav>

      <button onClick={() => navigate("/fire-system/monitoring")} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00A8A5] transition-colors mb-6 group">
        <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
        <span>Back</span>
      </button>

      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Select Report Type</h1>
          <p className="text-gray-500 text-sm mt-1">
            Choose a report type to view monthly records for{" "}
            <span className="font-semibold text-gray-700">{premiseName}</span>
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24"><DotLoader /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REPORT_TYPES.map(rt => {
            const total = totalCounts[rt.key] ?? 0;
            const styles = ICON_STYLES[rt.key] ?? { bg: "bg-gray-50", text: "text-gray-500", accent: "bg-gray-400", border: "border-gray-200" };
            return (
              <div
                key={rt.key}
                className="bg-white rounded-xl shadow-sm border-2 border-gray-100 hover:border-[#00A8A5] hover:shadow-lg hover:scale-[1.01] transition-all duration-200 overflow-hidden flex flex-col"
              >
                {/* Card header */}
                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`flex items-center justify-center w-16 h-16 rounded-2xl ${styles.bg} ${styles.text}`}>
                      {REPORT_ICONS[rt.key]}
                    </div>
                    {total > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#00A8A5]/10 text-[#00A8A5]">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        {total} total
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-400">No data</span>
                    )}
                  </div>
                  <h2 className="text-gray-900 font-bold text-lg mb-1">{rt.label}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed">{rt.description}</p>
                </div>

                {/* Card footer — two action buttons */}
                <div className="border-t border-gray-100 grid grid-cols-2 divide-x divide-gray-100">
                  <button
                    onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${rt.key}`)}
                    className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-[#00A8A5] hover:bg-[#00A8A5]/5 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    By Month
                  </button>
                  <button
                    onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${rt.key}/trends`)}
                    className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:text-[#00A8A5] transition-colors"
                    disabled={total === 0}
                    title={total === 0 ? "No data available for trends" : "View trends"}
                  >
                    <TrendIcon />
                    Trends
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
export default ReportTypeSelectionPage;
